import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Building2, Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function Branches() {
  const { user } = useAuth();
  const isAdmin = user?.role === "it_admin";
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", code: "" });
  const [busy, setBusy] = useState(false);
  const [delTarget, setDelTarget] = useState(null);

  const load = () => api.get("/branches").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ name: "", code: "" }); setOpen(true); };
  const openEdit = (b) => { setEditing(b); setForm({ name: b.name, code: b.code || "" }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) await api.put(`/branches/${editing.id}`, form);
      else await api.post("/branches", form);
      toast.success(editing ? "Cabang diperbarui." : "Cabang ditambahkan.");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    try { await api.delete(`/branches/${delTarget.id}`); toast.success("Cabang dihapus."); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setDelTarget(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <Building2 className="h-6 w-6 text-brand" /> Master Cabang
          </h2>
          <p className="text-sm text-muted-foreground">Kelola data cabang wilayah Lombok ({rows.length} cabang).</p>
        </div>
        {isAdmin && (
          <Button onClick={openCreate} className="bg-brand hover:bg-emerald-800" data-testid="add-branch-btn">
            <Plus className="h-4 w-4 mr-2" /> Tambah Cabang
          </Button>
        )}
      </div>

      <Card className="p-4">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Kode</TableHead>
                <TableHead>Nama Cabang</TableHead>
                <TableHead>Status</TableHead>
                {isAdmin && <TableHead className="text-right">Aksi</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((b) => (
                <TableRow key={b.id} data-testid={`branch-row-${b.id}`}>
                  <TableCell className="font-mono text-sm">{b.code || "-"}</TableCell>
                  <TableCell className="font-medium">{b.name}</TableCell>
                  <TableCell>
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${b.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
                      {b.is_active ? "Aktif" : "Nonaktif"}
                    </span>
                  </TableCell>
                  {isAdmin && (
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="icon" variant="ghost" onClick={() => openEdit(b)} data-testid={`edit-branch-${b.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => setDelTarget(b)} className="text-red-600 hover:bg-red-50" data-testid={`delete-branch-${b.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="branch-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Cabang" : "Tambah Cabang"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label>Nama Cabang</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1.5" data-testid="branch-name-input" />
            </div>
            <div>
              <Label>Kode (opsional)</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="mt-1.5" data-testid="branch-code-input" placeholder="CB01" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={busy} className="bg-brand hover:bg-emerald-800" data-testid="save-branch-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? "Simpan" : "Tambah")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus cabang {delTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>Cabang yang masih dipakai user tidak dapat dihapus.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-branch">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
